<script>
    import { onMount, onDestroy } from 'svelte'
    import { SvelteSet } from 'svelte/reactivity'
    import Modal from 'views/components/modals/Modal.svelte'
    import Button from 'views/components/button/Button.svelte'
    import Avatar from 'views/components/avatar/Avatar.svelte'
    import StudentModel from 'models/StudentModel'
    import Util from 'util/util'

    let isModalOpen = $state(true)
    let groups = $state([])
    const submittingIds = new SvelteSet()

    const request = loadPendingActions()

    // Keep the list live across tabs: a new request coming in, or one being handled
    // elsewhere, pushes this event to every teacher of the affected group.
    onMount(() => {
        Backbone.Model.user.on('group-pending-actions-changed', loadPendingActions)
    })

    onDestroy(() => {
        Backbone.Model.user.off('group-pending-actions-changed', loadPendingActions)
    })

    function getJSON(url) {
        return new Promise((resolve, reject) => {
            jQuery.getJSON(url, resolve).fail(reject)
        })
    }

    async function loadPendingActions() {
        const counts = await getJSON('/users/get_groups_with_pending_actions.json')
        const groupsWithRequests = counts.filter((group) => group.pending_students > 0)

        const details = await Promise.all(
            groupsWithRequests.map((group) => getJSON(`/users/get_pending_actions_for_group/${group.id}.json`))
        )

        groups = groupsWithRequests
            .map((group, index) => ({
                id: group.id,
                model: Backbone.Collection.groups.get(group.id),
                students: details[index].pending_students
            }))
            .filter((group) => group.model && group.students.length > 0)
    }

    function studentName(student) {
        return Util.lastNameFirstName(student.first_name, student.prefix, student.sortable_last_name)
    }

    function removeStudent(groupId, studentId) {
        groups = groups
            .map((group) => {
                if (group.id !== groupId) {
                    return group
                }
                return { ...group, students: group.students.filter((student) => student.id !== studentId) }
            })
            .filter((group) => group.students.length > 0)
    }

    function acceptStudent(group, student) {
        submittingIds.add(student.id)

        jQuery.post({
            url: '/users/grant_group_access.json',
            data: {
                group_id: group.id,
                user_ids_approved: [student.id]
            },
            success: () => {
                // Update the global groups collection with the newly added student
                const studentModel = new StudentModel({ ...student, group_id: group.id })
                group.model.students.addStudentToGroup(studentModel)

                removeStudent(group.id, student.id)
            },
            complete: () => {
                submittingIds.delete(student.id)
            }
        })
    }

    function denyStudent(group, student) {
        submittingIds.add(student.id)

        jQuery.post({
            url: '/users/grant_group_access.json',
            data: {
                group_id: group.id,
                user_ids_rejected: [student.id]
            },
            success: () => {
                removeStudent(group.id, student.id)
            },
            complete: () => {
                submittingIds.delete(student.id)
            }
        })
    }
</script>

<Modal
    title={window.i18n.gettext('Access requests')}
    canBeClosedWithEscapeKey={true}
    bind:isOpen={isModalOpen}
    {request}
>
    {#if groups.length}
        <div class="groups">
            {#each groups as group (group.id)}
                <section>
                    <div class="group-header">
                        {group.model.get('name')}
                        <span class="course">{group.model.getCourseModel().get('name')}</span>
                    </div>

                    <div class="students">
                        {#each group.students as student (student.id)}
                            <div class="student-row">
                                <div class="student">
                                    <Avatar avatar={student.avatar} showOnlineState={false}></Avatar>
                                    <div class="student-details">
                                        <span class="name">{studentName(student)}</span>
                                        {#if student.email}
                                            <span class="email">{student.email}</span>
                                        {/if}
                                    </div>
                                </div>
                                <div class="actions">
                                    <div class="accept">
                                        <Button
                                            label={window.i18n.gettext('Accept')}
                                            theme="transparent"
                                            icon="checkmark-circle-fill"
                                            disabled={submittingIds.has(student.id)}
                                            callback={() => acceptStudent(group, student)}
                                        ></Button>
                                    </div>
                                    <div class="deny">
                                        <Button
                                            label={window.i18n.gettext('Deny')}
                                            theme="transparent"
                                            icon="cross-circle"
                                            disabled={submittingIds.has(student.id)}
                                            callback={() => denyStudent(group, student)}
                                        ></Button>
                                    </div>
                                </div>
                            </div>
                        {/each}
                    </div>
                </section>
            {/each}
        </div>
    {:else}
        <p class="empty">{window.i18n.gettext('There are no pending access requests.')}</p>
    {/if}

    {#snippet buttons()}
        <Button
            label={window.i18n.gettext('Close')}
            theme="secondary"
            callback={() => { isModalOpen = false }}
        ></Button>
    {/snippet}
</Modal>

<style lang="scss">/*
* Scut, a collection of Sass utilities
* to ease and improve our implementations of common style-code patterns.
* v1.0.1
* Docs at http://davidtheclark.github.io/scut
*/
/* TODO implement tooltip arrow */
.groups {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.group-header {
  font-size: 14px;
  font-variant-caps: all-small-caps;
  letter-spacing: 1px;
  line-height: 12px;
  color: hsl(205, 60%, 44%);
  margin-bottom: 8px;
  color: hsl(205, 60%, 44%);
}
.group-header .course {
  margin-left: 8px;
  color: rgba(18, 45, 64, 0.8);
  font-weight: 400;
}

.students {
  display: flex;
  flex-direction: column;
  border: 1px solid hsl(27, 15%, 93%);
  border-radius: 12px;
  overflow: hidden;
}

.student-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 4px 4px 12px;
}
.student-row + .student-row {
  border-top: 1px solid hsl(27, 15%, 93%);
}

.student {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.student .student-details {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.student .name {
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.student .email {
  font-size: 12px;
  overflow: hidden;
  color: rgba(18, 45, 64, 0.8);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.actions {
  display: flex;
  flex-shrink: 0;
  gap: 4px;
}
.actions .accept :global([data-button-icon]) {
  color: #3fd17d;
}
.actions .deny :global([data-button-icon]) {
  color: #fc7787;
}

.empty {
  font-size: 16px;
  padding: 24px 0;
  color: rgba(18, 45, 64, 0.8);
  text-align: center;
}</style>
